import { useCallback, useEffect, useRef } from "react";

import { useAiCurriculumUIStore } from "@src/stores/zustand/ai-curriculum-ui.store";

import { DRAFT_SAVE_DEBOUNCE_MS } from "./ai-curriculum.constants";

/**
 * Persists the wizard draft, debounced so free-text fields don't write on
 * every keystroke (ENG-23196).
 */
export const useAiCurriculumDraft = (orgCode, classroomId, draft, enabled) => {
  const saveDraft = useAiCurriculumUIStore((s) => s.saveDraft);

  const latest = useRef({ enabled: false });

  // `enabled` is read from the ref, not captured: a flush firing after the
  // wizard closed would otherwise rewrite the draft reset() just deleted.
  const flush = useCallback(() => {
    const pending = latest.current;
    if (!pending.enabled) return;
    saveDraft(pending.orgCode, pending.classroomId, pending.draft);
  }, [saveDraft]);

  useEffect(() => {
    latest.current = { orgCode, classroomId, draft, enabled };
    if (!enabled) return undefined;

    const timerId = setTimeout(flush, DRAFT_SAVE_DEBOUNCE_MS);
    // A refresh can land inside the debounce window; sessionStorage writes are
    // synchronous, so pagehide still catches the last keystrokes.
    window.addEventListener("pagehide", flush);

    return () => {
      clearTimeout(timerId);
      window.removeEventListener("pagehide", flush);
    };
  }, [orgCode, classroomId, draft, enabled, flush]);

  // Unmount only — `flush` is stable, so this doesn't run on every edit.
  useEffect(() => () => flush(), [flush]);
};
